import type { BYOKKeyKind, BYOKStatus } from '@developerz/domain';
import {
  IconAnthropic,
  IconGemini,
  IconKimi,
  IconMiniMax,
  IconOpenAI,
  IconOpenRouter,
  IconPlug,
  type IconSvgProps,
} from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { t } from '../i18n';
import type { KeyCapacityState } from '../lib/key-capacity';
import type { KeyRow } from '../lib/keys';
import { absolute } from '../lib/time';
import { UNSET } from '../lib/unset';
import { KeyCapacity } from './KeyCapacity';
import { KeyModelTags } from './KeyModelTags';
import { KeyPriority } from './KeyPriority';
import { StatusPill, type StatusTone } from './StatusPill';
import { Button } from './ui/Button';

export type KeyCardProps = {
  readonly row: KeyRow;
  readonly onRevoke: (row: KeyRow) => void;
  /**
   * Optional re-verify action. When passed, a Verify control renders beside
   * Revoke for any non-revoked key. Both key surfaces pass it — `MyKeys` and the
   * org pool (`AccountKeys`, whose `valid` keys the funnel's next settle reads as
   * `key_set`). Optional because the prop, not the caller list, is the contract.
   */
  readonly onVerify?: (row: KeyRow) => void;
  /** Optional rotate action (#971) — renders iff passed, like `onVerify`. */
  readonly onRotate?: (row: KeyRow) => void;
  /**
   * Re-rank the key in its pool (plan 102 slice 05b) — the editor renders iff
   * passed, on a non-revoked key. The container passes it to the callers the
   * route admits: owner/admin on the org pool, the member on their own.
   */
  readonly onSetPriority?: ((row: KeyRow, priority: number) => void) | undefined;
  /** A priority save in flight for this row. */
  readonly priorityBusy?: boolean | undefined;
  /**
   * This key's reading of `GET /v1/billing/capacity` (#6863). The "Jobs at once"
   * block renders iff passed: the ORG pool passes it, My keys does not.
   */
  readonly capacityRead?: KeyCapacityState | undefined;
  /**
   * Declare the key's concurrency (`null` = Use default). The editor renders iff
   * passed, on a non-revoked key; the container passes it to admins only.
   */
  readonly onSetCapacity?: ((row: KeyRow, capacity: number | null) => void) | undefined;
  /** A capacity save in flight for this row. */
  readonly capacityBusy?: boolean | undefined;
};

const STATUS_LABEL: Record<BYOKStatus, string> = {
  unverified: 'account_keys.status_unverified',
  valid: 'account_keys.status_valid',
  invalid: 'account_keys.status_invalid',
  revoked: 'account_keys.status_revoked',
  // Recoverable (#2844): the credential is fine, the wallet/throttle is not.
  // WARNING, not danger — danger is a verdict on the key, and neither of these is.
  credits_exhausted: 'account_keys.status_credits_exhausted',
  rate_limited: 'account_keys.status_rate_limited',
};

const STATUS_TONE: Record<BYOKStatus, StatusTone> = {
  unverified: 'warning',
  valid: 'success',
  invalid: 'danger',
  revoked: 'neutral',
  credits_exhausted: 'warning',
  rate_limited: 'warning',
};

// Billing-nature badge (arc 101 slice 03): `subscription` = a flat-rate coding
// plan (Kimi Code / z.ai coding), `metered` = pay-per-token. A `null` kind (a
// legacy row never classified) shows no badge.
const KIND_LABEL: Record<BYOKKeyKind, string> = {
  metered: 'account_keys.kind_metered',
  subscription: 'account_keys.kind_subscription',
};

// Provider display names (#801 pain point #4): the raw enum value is lower-case
// ("openai"/"anthropic"); render the brand-cased label instead. Unknown providers
// fall back to the raw value. Shared with the add-key form + the pref card.
//
// A `Map`, not a `Record`: the lookup happens by a server-supplied `provider`,
// and a `Record` indexed by a name inherits `constructor`, `toString`,
// `valueOf`, `__proto__`, `hasOwnProperty` from `Object.prototype` — each truthy,
// so the `??` fallback does not catch it. The looked-up value is then `.slice`'d
// to derive a monogram, and a `Function` has no `.slice`. Same prototype-fix
// pattern as `PROVIDER_ICON` (#4127).
const PROVIDER_LABEL: ReadonlyMap<string, string> = new Map([
  ['anthropic', 'Anthropic'],
  ['openai', 'OpenAI'],
  ['openrouter', 'OpenRouter'],
  ['gemini', 'Gemini'],
  ['google', 'Gemini'],
  ['zai', 'z.ai'],
  ['kimi', 'Kimi'],
  ['cerebras', 'Cerebras'],
  ['minimax', 'MiniMax'],
  // OpenAI-wire bearer third parties (arc 101 slice 03b) — #935's coverage gate
  // fails the DOMAIN enum against this table, so a new `BYOKProvider` needs a
  // label here the moment it lands, never a raw enum value on screen.
  ['groq', 'Groq'],
  ['xai', 'xAI'],
  ['deepseek', 'DeepSeek'],
  ['together', 'Together AI'],
  ['fireworks', 'Fireworks AI'],
  // Alibaba Cloud Model Studio (DashScope), OpenAI wire (arc 101 slice 02) —
  // named for the console the key and its workspace endpoint come from.
  ['alibaba', 'Alibaba Model Studio'],
  // #1904: the router gateway renders as itself — never as the `custom` it
  // structurally resembles and never as `anthropic` (its wire).
  ['router', 'Router'],
  ['custom', 'Custom'],
]);

export function providerLabel(provider: string): string {
  return PROVIDER_LABEL.get(provider.toLowerCase()) ?? provider;
}

/** Shared account title; the keys page keeps its provider heading and model separate. */
export function keyDisplayTitle(key: {
  label: string | null;
  provider: string;
  model?: string | null;
}): string {
  return key.label ?? providerLabel(key.provider) + (key.model ? ` · ${key.model}` : '');
}

// Brand glyph per provider (#801 pain point #4, completed in #935). Every BYOK
// provider with a published mark now has one; `cerebras` and `zai` keep the
// monogram because neither ships a normalized 24x24 glyph we can source, and a
// hand-traced approximation of someone's logo is worse than a clean letter.
//
// `custom` is not a brand at all — it's "any OpenAI-compatible endpoint" — so it
// takes the generic plug rather than a "C" that would read as Cerebras.
//
// A `Map`, not a `Record`: the looked-up value is passed to `<Dynamic component={…}>`,
// and a `Record` indexed by a server-supplied name inherits `constructor`,
// `toString`, `valueOf`, `__proto__` and `hasOwnProperty` from `Object.prototype` — each
// truthy, so a `??` fallback does not catch it and the inherited function would be
// rendered as a component (#4127). A `Map` has no prototype chain to index into.
const PROVIDER_ICON: ReadonlyMap<string, (props: IconSvgProps) => JSX.Element> = new Map([
  ['anthropic', IconAnthropic],
  ['gemini', IconGemini],
  ['google', IconGemini],
  ['openai', IconOpenAI],
  ['openrouter', IconOpenRouter],
  ['kimi', IconKimi],
  ['minimax', IconMiniMax],
  ['custom', IconPlug],
]);

/**
 * The provider's mark, in a fixed tile so a brand glyph and a monogram line up
 * to the same box — a list mixing the two reads as one column, not as a ragged
 * set of logos. `data-provider` is the styling hook for the per-brand tint.
 *
 * ponytail: the monogram fallback stays even now that most providers have a
 * mark — it is what lets a new `BYOKProvider` land without a logo hunt, and what
 * covers the two vendors with no sourceable glyph. Add a `PROVIDER_ICON` entry
 * when a mark exists; nothing else moves.
 */
export function ProviderMark(props: { readonly provider: string }): JSX.Element {
  const key = (): string => props.provider.toLowerCase();
  const Glyph = (): ((p: IconSvgProps) => JSX.Element) | undefined => PROVIDER_ICON.get(key());
  return (
    <span class="provider-mark" data-provider={key()} aria-hidden="true">
      <Show
        when={Glyph()}
        fallback={
          <span class="provider-mark__monogram">
            {providerLabel(props.provider).slice(0, 1).toUpperCase()}
          </span>
        }
      >
        {(Icon) => <Dynamic component={Icon()} size="1rem" />}
      </Show>
    </span>
  );
}

export function KeyCard(props: KeyCardProps): JSX.Element {
  const active = (): boolean => props.row.status !== 'revoked';
  const lastVerified = (): string =>
    props.row.last_verified_iso ? absolute(props.row.last_verified_iso) : UNSET;
  return (
    <li
      class="card key-card"
      classList={{ 'card--footered': active() }}
      data-status={props.row.status}
    >
      {/* divs, not header/footer: outside sectioning content those map to
          banner/contentinfo and duplicate the shell's landmarks (axe). */}
      <div class="card__header">
        <div class="key-card__head">
          <h2 class="card__title key-card__provider">
            <ProviderMark provider={props.row.provider} />
            {providerLabel(props.row.provider)}
          </h2>
          <div class="key-card__badges">
            <Show when={props.row.kind}>
              {(kind) => (
                <span class="badge key-card__kind" data-kind={kind()}>
                  {t(KIND_LABEL[kind()])}
                </span>
              )}
            </Show>
            <StatusPill tone={STATUS_TONE[props.row.status]}>
              {t(STATUS_LABEL[props.row.status])}
            </StatusPill>
          </div>
        </div>
        {/* The connection identity. The mock masks a key fingerprint here, but the
            sealed ciphertext exposes no prefix/last4 — anchor on the model instead. */}
        <p class="card__desc key-card__model">{props.row.model}</p>
      </div>

      <div class="card__content key-card__meta">
        <span>{t('account_keys.last_verified', { when: lastVerified() })}</span>
        <KeyPriority
          keyId={props.row.id}
          priority={props.row.priority}
          busy={props.priorityBusy}
          onSave={
            active() && props.onSetPriority
              ? (priority) => props.onSetPriority?.(props.row, priority)
              : undefined
          }
        />
        <Show when={props.capacityRead}>
          {(state) => (
            <KeyCapacity
              keyId={props.row.id}
              declared={props.row.capacity}
              state={state()}
              busy={props.capacityBusy}
              onSave={
                active() && props.onSetCapacity
                  ? (capacity) => props.onSetCapacity?.(props.row, capacity)
                  : undefined
              }
            />
          )}
        </Show>
        {/* `byok_keys.last_error`, verbatim. A clean promote clears it
            server-side (`markKeyValid`), so a green card that never hit a
            model-parked verify has nothing here — but #2844 leaves a
            `last_error` ON a valid row when the CREDENTIAL verified and the MODEL
            did not (the model's catalogue row is parked; the reason names it), so
            the value itself is the gate, not the status. The frame names neither a
            party nor a verify (#6657): its writers (`packages/db` `queries/byok/`)
            are `markKeyInvalid`, `recordTransportFailure`, `markKeyCooling` and
            `markKeyValidParkingModel` (`verification.ts`), `recordUnprovenProbe`
            (`unproven.ts`) and `markKeySuspect` (`suspect.ts`); `markKeyCooling` and
            `markKeySuspect` are also written by task runs, not verifies. Each
            writer's own sentence says who failed. Rendered as text — it is an
            error body, never markup.

            THE FRAME NEVER BRANCHES. A persisted row carries no probe fault
            (`byok_keys` has no column for one, #6657), so the card cannot tell
            an egress refusal from any other reason — and it must not sniff the
            reason text to try: a sentence that changes is a sentence a card
            stops reading. Where the fault DOES travel, the copy branches on it:
            the verify toast (`lib/key-verify.ts`) and the terminal's
            `billing.key.model_parked` row. */}
        <Show when={props.row.last_error}>
          {(reason) => (
            <span class="key-card__error">
              {t('account_keys.last_error', { reason: reason() })}
            </span>
          )}
        </Show>
        {/* What the user tagged, as the SERVER answered it (arc 101 slice 02). */}
        <KeyModelTags models={props.row.models} />
      </div>

      <Show when={active()}>
        <div class="card__footer key-card__actions">
          <Show when={props.onVerify}>
            {(onVerify) => (
              <Button variant="outline" size="sm" onClick={() => onVerify()(props.row)}>
                {t('account_keys.verify')}
              </Button>
            )}
          </Show>
          <Show when={props.onRotate}>
            {(onRotate) => (
              <Button variant="outline" size="sm" onClick={() => onRotate()(props.row)}>
                {t('account_keys.rotate')}
              </Button>
            )}
          </Show>
          <Button variant="ghost" size="sm" onClick={() => props.onRevoke(props.row)}>
            {t('account_keys.revoke')}
          </Button>
        </div>
      </Show>
    </li>
  );
}
